import { useRouter } from 'next/router';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import Link from 'next/link';

import {
  BoldEnum,
  GiftCatalogue,
  GiftsBanner,
  Heading,
  HeadingEnum,
  Text,
  TextColorEnum,
  TextEnum,
} from '@desquared/wave-components-library';
import { GIFTS_DEFAULT_PAGE_SIZE, getV3GiftsPaginated, submitGiftOrder } from '@/api/Gifts';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold';
import { getGiftReceiptDetails, transformGift } from '@/utils/gifts';
import { GiftsSideSheetStore } from '@/store/sideSheet';
import GiftsAside from '../GiftsAside/GiftsAside';
import { formatNumber } from '@/utils/amountUtils';
import { getOrFetchConfiguration } from '@/utils/getConfiguration';
import useGiftDeliveryOptions from '@/hooks/useGiftDeliveryOptions';
import User from '@/store/user';
import isEmpty from 'lodash/isEmpty';

import styles from './GiftsList.module.css';
import GiftReceipt from '../GiftReceipt/GiftReceipt';
import { useTranslations } from 'next-intl';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useDateFormatting } from '@/contexts/DateFormattingProvider';
import useGetDefaultProductImage from '@/hooks/useGetDefaultProductImage.js';

export default function GiftsList({ initialGifts, giftsConfig }) {
  const observer = useRef(null);
  const router = useRouter();
  const t = useTranslations();

  const isMobile = useWindowUnderThreshold(850);
  const [config, setConfig] = useState();
  const [user, setUser] = useState();
  const [selectedGift, setSelectedGift] = useState(null);
  const [selectedDeliveryIndex, setSelectedDeliveryIndex] = useState(0);
  const [comment, setComment] = useState('');
  const [showReceipt, setShowReceipt] = useState(null);
  const [receiptDetails, setReceiptDetails] = useState();
  const [showStores, setShowStores] = useState(false);
  const [storeId, setStoreId] = useState(undefined);
  const [sideSheet, setSideSheet] = useState(false);
  const { timeFormat } = useDateFormatting();
  const defaultProductImage = useGetDefaultProductImage(0);

  const { deliveryOptions } = useGiftDeliveryOptions(config, selectedDeliveryIndex);

  const onSelectGift = _gift => {
    setSelectedGift(_gift);
    GiftsSideSheetStore.open();
  };

  const {
    data: giftData,
    isLoading: isGiftLoading,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['gifts', router.locale],
    queryFn: ({ pageParam }) => {
      return getV3GiftsPaginated({ available: true, pageNumber: pageParam }, router.locale);
    },
    initialPageParam: 1,
    initialData: { pageParams: [0], pages: [{ data: initialGifts, pageNumber: 0, pageSize: GIFTS_DEFAULT_PAGE_SIZE }] },
    staleTime: 0,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.pageNumber + 1 >= lastPage.totalPages) return undefined;

      return lastPage.pageNumber + 1;
    },
    select: data =>
      data.pages
        .flatMap(p => p.data)
        .map(gift => transformGift(gift, user?.totalLoyaltyPoints, t))
        .map(g => ({
          ...g,
          slug: `gifts/${g.slug}`,
        })),
  });

  useEffect(() => {
    async function getConfiguration() {
      const configuration = await getOrFetchConfiguration();
      setConfig(configuration);
      return;
    }

    const GiftsSideSheetStore$ = GiftsSideSheetStore.subscribe(setSideSheet);
    const user$ = User.subscribe(_user => {
      if (isEmpty(_user)) {
        router.replace('/');
        return;
      }
      setUser(_user);
    });
    getConfiguration();

    return () => {
      GiftsSideSheetStore$.unsubscribe();
      user$.unsubscribe();
    };
  }, []);

  const onConfirmRedemption = () => {
    const deliveryOption = deliveryOptions.find(option => option.index === selectedDeliveryIndex);
    submitGiftOrder(selectedGift.id, deliveryOption?.type || 'TAKEAWAY', comment, storeId).then(result => {
      const _receiptDetails = getGiftReceiptDetails(result, t, timeFormat);
      setReceiptDetails(_receiptDetails);
      setShowReceipt(true);
    });
  };

  const lastResultItemRef = useCallback(
    node => {
      if (!node) return;
      if (isGiftLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      });

      observer.current.observe(node);
    },
    [isGiftLoading, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    if (selectedDeliveryIndex === 1) setShowStores(true);
    else setShowStores(false);
  }, [selectedDeliveryIndex]);

  return (
    <>
      <section className={styles.bannerContainer}>
        <Heading type={HeadingEnum.HeadingM} bold={BoldEnum.Bold} classOption={styles.title}>
          {giftsConfig?.gifts_name || t('gifts.page_title')}
        </Heading>
        <Text type={TextEnum.Body} color={TextColorEnum.Gray1} classOption={styles.subTitle}>
          <span>{t('gifts.page_subtitle1')}</span>{' '}
          <b>
            {formatNumber(user?.totalLoyaltyPoints)} {t('gifts.page_subtitle2')}
          </b>
          <span>{t('gifts.page_subtitle3')}</span>
        </Text>
        <GiftsBanner isMobile={isMobile} mobileImage={giftsConfig?.mobile_image?.url} image={giftsConfig?.image?.url} />
      </section>
      {isGiftLoading ? (
        <GiftCatalogue isLoading loaderTilesAmount={GIFTS_DEFAULT_PAGE_SIZE} />
      ) : (
        <>
          <GiftCatalogue
            title={t('gifts.gifts_title')}
            gifts={giftData}
            onSelectGift={onSelectGift}
            lastItemRef={lastResultItemRef}
            LinkComponent={props => {
              return <Link prefetch={false} href={props.href} {...props} />;
            }}
            hasRoundedCorners={process.env.hasRoundedCorners}
            defaultProductImage={defaultProductImage}
          />
          {isFetchingNextPage ? <GiftCatalogue isLoading loaderTilesAmount={4} /> : null}
        </>
      )}
      {/* </div> */}
      {sideSheet && (
        <GiftsAside
          firstName={user?.firstName}
          lastName={user?.lastName}
          onConfirmRedemption={onConfirmRedemption}
          alertText={giftsConfig?.alert_text}
          onSelectDeliveryOption={index => setSelectedDeliveryIndex(index)}
          onSelectStore={setStoreId}
          listAddress={[]}
          onSelectAllStores={() => {
            GiftsSideSheetStore.close();
            // SideSheetStore.open();
          }}
          deliveryOptions={deliveryOptions}
          onChangeComment={setComment}
          comment={comment}
          onClose={() => setComment('')}
          showStores={showStores}
        />
      )}
      <GiftReceipt
        showModal={showReceipt}
        setShowModal={setShowReceipt}
        greeting={t('gifts.receipt.greeting')}
        message={t('gifts.receipt.message')}
        receiptDetailsList={receiptDetails}
      />
    </>
  );
}
